/**
 * Utility Functions
 *
 * Common utilities used across the application.
 */

import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with clsx
 *
 * Combines clsx for conditional classes with tailwind-merge
 * to properly handle conflicting Tailwind classes.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format currency in Costa Rican Colones
 */
export function formatCurrency(
  amount: number,
  currency: 'CRC' | 'USD' = 'CRC'
): string {
  const locale = currency === 'CRC' ? 'es-CR' : 'en-US'
  const currencyCode = currency

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format a monetary amount for spreadsheet/CSV exports.
 *
 * Plain number with comma thousands separator (en-US), with NO currency symbol
 * and NO spaces, so Excel parses it as a number cleanly and the value never
 * carries a stray "$"/"₡" or non-breaking space. Shows a minimum of 2 decimals
 * and up to 5 when the value has more precision (e.g. IVA round-trip prices).
 */
export function formatAmountForExport(amount: number | null | undefined): string {
  const value = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  }).format(value)
}

/**
 * Format date for display
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('es-CR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

/**
 * Format date and time for display
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('es-CR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/**
 * Format percentage
 */
export function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`
}

/**
 * Compose a client display name with an optional free-text note.
 * On screen the note is wrapped in double quotes: `Juan Pérez "entrega lunes"`.
 */
export function formatClientWithNote(
  name: string | null | undefined,
  note: string | null | undefined,
  fallback = ''
): string {
  const display = (name ?? '').trim() || fallback
  const trimmedNote = (note ?? '').trim()
  return trimmedNote ? `${display} "${trimmedNote}"` : display
}

/**
 * Same as formatClientWithNote but uses parentheses, intended for CSV/Excel
 * exports where embedded double quotes complicate CSV escaping.
 */
export function formatClientWithNoteForExport(
  name: string | null | undefined,
  note: string | null | undefined,
  fallback = ''
): string {
  const display = (name ?? '').trim() || fallback
  const trimmedNote = (note ?? '').trim()
  return trimmedNote ? `${display} (${trimmedNote})` : display
}

/**
 * Debounce function
 *
 * Delays function execution until after wait milliseconds
 * have elapsed since the last call.
 */
export function debounce<Args extends unknown[]>(
  fn: (...args: Args) => void,
  wait: number
): (...args: Args) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return function (...args: Args) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => fn(...args), wait)
  }
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}

/**
 * Parse error message from API response
 */
export function parseApiError(error: unknown): string {
  if (typeof error === 'string') {
    return error
  }

  // Axios error
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { detail?: string | string[] } } }
    const detail = axiosError.response?.data?.detail

    if (Array.isArray(detail)) {
      return detail.join(', ')
    }
    if (typeof detail === 'string') {
      return detail
    }
  }

  // Generic error
  if (error instanceof Error) {
    return error.message
  }

  return 'An unexpected error occurred'
}

/**
 * Identification type labels for Costa Rica
 */
export const IDENTIFICATION_TYPES: Record<string, string> = {
  '01': 'Cédula Física',
  '02': 'Cédula Jurídica',
  '03': 'NITE',
  '04': 'DIMEX',
  '05': 'Extranjero',
}

/**
 * Get identification type label
 */
export function getIdentificationTypeLabel(type: string): string {
  return IDENTIFICATION_TYPES[type] || type
}

/**
 * Bill status labels
 */
export const BILL_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  finalized: 'Finalizada',
  paid: 'Pagada',
  credit: 'Crédito',
  cancelled: 'Anulada',
  continue_later: 'Continuar después',
}

/**
 * Bill type labels
 */
export const BILL_TYPE_LABELS: Record<string, string> = {
  ticket: 'Tiquete',
  invoice: 'Factura',
  credit_note: 'Nota de Crédito',
  proforma: 'Proforma',
}

/**
 * Payment type labels
 */
export const PAYMENT_TYPE_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  credit: 'Crédito',
  sinpe: 'SINPE',
  check: 'Cheque',
}
